import React from 'react';
import { Shield, Lock, FileCheck, CheckCircle, Search, FileText, Wrench, Award, TrendingUp, BarChart3, AlertCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const CmmcServices = () => {
  const navigate = useNavigate();

  const compliancePath = [
    {
      icon: Search,
      title: 'Initial Assessment',
      description: 'Comprehensive evaluation of your current network architecture against the NIST 800-171 framework.',
      details: [
        'Deep dive into security posture',
        'Gap analysis and baseline measurement',
        'NIST 800-171 compliance review',
        'Vulnerability identification'
      ],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FileText,
      title: 'Documentation Development',
      description: 'Creation of critical compliance documentation to outline security functions and identify gaps.',
      details: [
        'System Security Plan (SSP)',
        'Plan-of-Action & Milestones (PO&AM)',
        'Security gap documentation',
        'Compliance roadmap creation'
      ],
      gradient: 'from-purple-500 to-indigo-500'
    },
    {
      icon: Wrench,
      title: 'Remediation',
      description: 'Active implementation phase to address identified vulnerabilities and strengthen security controls.',
      details: [
        'Multi-factor authentication (MFA)',
        'Security controls implementation',
        'Hardware and software upgrades',
        'Vulnerability resolution'
      ],
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Award,
      title: 'Verification',
      description: 'Formal assessment through authorized Third-Party Assessment Organizations (C3PAO).',
      details: [
        'C3PAO selection and coordination',
        'Formal CMMC Assessment C3PAO',
        
      ],
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const businessBenefits = [
    {
      icon: FileCheck,
      title: 'Contract Eligibility',
      description: 'Certification is mandatory for DoD contracts, essential for business survival and growth in the defense sector.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      icon: BarChart3,
      title: 'Automated Oversight',
      description: 'AI-driven dashboards monitor security controls in real-time.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      icon: FileText,
      title: 'Evidence Collection',
      description: 'Streamlined systems automatically gather compliance proof, reducing manual documentation burden.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Resilience',
      description: 'Ongoing monitoring ensures security measures remain current and effective over time.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    }
  ];

  const handleScroll = (href) => {
    if (href.startsWith('#')) {
      navigate('/' + href);
    }
  };

  return (
    <section id="cmmc-services" className="pt-12 sm:pt-16 md:pt-20 pb-20 sm:pb-24 md:pb-32 bg-gradient-to-b from-blue-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider">
              <Shield className="w-4 h-4" />
              DoD Cybersecurity Compliance
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            CMMC <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-600"> Services</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Navigate the Cybersecurity Maturity Model Certification (CMMC) with confidence. We guide Defense Industrial Base contractors through the certification journey, to help ensure compliance with DoD requirements.
          </p>
        </div>

        {/* What is CMMC Section */}
        <div className="mb-20 sm:mb-24 md:mb-28">
          <div className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 rounded-2xl p-8 sm:p-12 shadow-lg border-2 border-blue-200/50">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">What is CMMC?</h3>
                <p className="text-slate-700 text-lg leading-relaxed">
                  The Cybersecurity Maturity Model Certification (CMMC) is a unified security standard required by the U.S. Department of Defense (DoD) for all contractors and subcontractors within the Defense Industrial Base (DIB). It ensures proper safeguarding of sensitive federal information, with security requirements scaled based on data sensitivity, primarily focusing on Controlled Unclassified Information (CUI).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* The Path to CMMC Readiness */}
        <div className="mb-20 sm:mb-24 md:mb-28">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">The Path to CMMC Readiness</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              A structured, multi-step process designed to move your business from its current security state to a fully verified, compliant infrastructure.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {compliancePath.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl font-bold text-blue-600/20">{index + 1}</span>
                        <h4 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {step.title}
                        </h4>
                      </div>
                      <p className="text-slate-600 leading-relaxed mb-4">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 pl-4 border-l-2 border-blue-200">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Critical Business Benefits */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">Critical Business Benefits</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              CMMC certification delivers strategic advantages beyond compliance, positioning your organization for success in the defense sector.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessBenefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div 
                  key={index} 
                  className={`${benefit.bgColor} border ${benefit.borderColor} rounded-xl p-6 hover:shadow-lg transition-all duration-300`}
                >
                  <div className={`w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                    <IconComponent className={`w-6 h-6 ${benefit.color}`} />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">
                    {benefit.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 rounded-3xl p-10 sm:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -ml-20 -mb-20"></div>
          <div className="relative z-10">
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Achieve CMMC Certification?</h3>
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              Our expert team will guide you through every step of the CMMC compliance journey, from initial assessment to final certification.
            </p>
            <Button 
              onClick={() => handleScroll('#contact')} 
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-blue-50 text-blue-700 font-bold px-8 py-4 rounded-xl hover:shadow-xl transition-all duration-300"
            >
              Start Your CMMC Journey
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CmmcServices;
